import type {
	WidgetBubbleAppearance,
	WidgetLauncherAppearance,
	WidgetMenuSettings,
	WidgetPanelAppearance,
	WidgetSettings,
} from '@livechat/shared';
import { defaultWidgetSettings } from '@livechat/shared';
import { type CSSProperties, useState } from 'react';

import { widgetAppearanceVariables } from '../widget-preview.js';

interface WidgetAppearanceEditorProps {
	brandingHtml: string;
	brandingRemovalAllowed: boolean;
	onChange: (settings: WidgetSettings) => void;
	value: WidgetSettings;
}

function ColourControl({
	label,
	onChange,
	value,
}: {
	label: string;
	onChange: (value: string) => void;
	value: string;
}) {
	return (
		<label className="colour-control">
			<span>{label}</span>
			<span className="colour-input-row">
				<input
					aria-label={`${label} picker`}
					onChange={(event) => onChange(event.currentTarget.value)}
					type="color"
					value={value}
				/>
				<input
					aria-label={`${label} hex value`}
					maxLength={7}
					onChange={(event) => onChange(event.currentTarget.value)}
					pattern="#[0-9a-fA-F]{6}"
					value={value}
				/>
			</span>
		</label>
	);
}

function RangeControl({
	label,
	max,
	min = 0,
	onChange,
	value,
}: {
	label: string;
	max: number;
	min?: number;
	onChange: (value: number) => void;
	value: number;
}) {
	return (
		<label>
			<span>
				{label}: {value}px
			</span>
			<input
				max={max}
				min={min}
				onChange={(event) => onChange(event.currentTarget.valueAsNumber)}
				type="range"
				value={value}
			/>
		</label>
	);
}

function ChatboxPreview({
	brandingHtml,
	settings,
}: {
	brandingHtml: string;
	settings: WidgetSettings;
}) {
	const [menuOpen, setMenuOpen] = useState(false);
	const [preview, setPreview] = useState<'chatbox' | 'offline-form'>('chatbox');
	const offline = preview === 'offline-form';
	const startTitle = offline ? settings.offlineForm.title : settings.startTitle;
	const startMessage = offline ? settings.offlineForm.description : settings.startMessage;
	const buttonLabel = offline ? settings.offlineForm.buttonLabel : settings.startButton;

	return (
		<div className="customisation-preview-shell">
			<div className="preview-toolbar" aria-label="Preview content">
				<strong>Preview</strong>
				<div>
					<button
						className={preview === 'chatbox' ? 'selected' : ''}
						onClick={() => setPreview('chatbox')}
						type="button"
					>
						Chatbox
					</button>
					<button
						className={preview === 'offline-form' ? 'selected' : ''}
						onClick={() => setPreview('offline-form')}
						type="button"
					>
						Offline Form
					</button>
				</div>
			</div>
			<div
				className={`customisation-preview ${preview}`}
				style={widgetAppearanceVariables(settings) as CSSProperties}
			>
				<div className="preview-widget-stage">
					<section
						className="preview-chat-panel"
						aria-label={`${offline ? 'Offline Form' : 'Chatbox'} preview`}
					>
						<header>
							<div className="preview-header-copy">
								<span className="preview-header-title">{settings.title}</span>
								<span className="preview-header-subtitle">{settings.subtitle}</span>
							</div>
							<div className="preview-header-actions">
								{settings.menu.visible && (
									<>
										<button
											aria-expanded={menuOpen}
											aria-label="Chat options"
											className="preview-header-action preview-menu-toggle"
											onClick={() => setMenuOpen((current) => !current)}
											type="button"
										>
											<svg viewBox="0 0 24 24" aria-hidden="true">
												<circle cx="5" cy="12" r="1.6" />
												<circle cx="12" cy="12" r="1.6" />
												<circle cx="19" cy="12" r="1.6" />
											</svg>
										</button>
										{menuOpen && (
											<div className="preview-chat-menu" role="menu">
												{settings.menu.emailTranscript && (
													<button
														className="preview-menu-item"
														type="button"
													>
														<svg viewBox="0 0 24 24" aria-hidden="true">
															<path d="M3.5 6.5h17v12h-17z" />
															<path d="m4 7 8 6 8-6" />
														</svg>
														<span>Email chat transcript</span>
													</button>
												)}
												{settings.menu.sound && (
													<button
														className="preview-menu-item"
														type="button"
													>
														<svg viewBox="0 0 24 24" aria-hidden="true">
															<path d="M5 10v4h4l5 4V6l-5 4H5z" />
															<path d="M17 9c1.3 1.7 1.3 4.3 0 6" />
															<path d="M19.5 6.5c3 3.2 3 7.8 0 11" />
														</svg>
														<span>Sound</span>
													</button>
												)}
												{settings.menu.endChat && (
													<button
														className="preview-menu-item"
														type="button"
													>
														<svg viewBox="0 0 24 24" aria-hidden="true">
															<path d="m7 7 10 10M17 7 7 17" />
														</svg>
														<span>End chat</span>
													</button>
												)}
											</div>
										)}
									</>
								)}
								<button
									aria-label="Minimize chat"
									className="preview-header-action"
									title="Minimize chat"
									type="button"
								>
									<svg viewBox="0 0 24 24" aria-hidden="true">
										<path d="m6 9 6 6 6-6" />
									</svg>
								</button>
							</div>
						</header>
						<div className="preview-content">
							<div className="preview-start-form">
								{(startTitle || startMessage) && (
									<div className="preview-welcome">
										{startTitle && <strong>{startTitle}</strong>}
										{startMessage && <span>{startMessage}</span>}
									</div>
								)}
								<label className="preview-field">
									<span>Your name</span>
									<input aria-label="Preview visitor name" readOnly />
								</label>
								<label className="preview-field">
									<span>Email address</span>
									<input
										aria-label="Preview visitor email"
										readOnly
										type="email"
									/>
								</label>
								<label className="preview-field">
									<span>Department</span>
									<select aria-label="Preview department" defaultValue="support">
										<option value="support">Customer support</option>
									</select>
								</label>
								<label className="preview-field">
									<span>How can we help?</span>
									<textarea
										aria-label="Preview message"
										placeholder={settings.messagePlaceholder}
										readOnly
									/>
								</label>
								<button className="preview-primary" type="button">
									{buttonLabel}
								</button>
							</div>
						</div>
						{!settings.hideBranding && (
							<footer
								className="preview-branding"
								dangerouslySetInnerHTML={{ __html: brandingHtml }}
							/>
						)}
					</section>
					<button
						aria-expanded="true"
						aria-label="Close preview chat"
						className="preview-launcher"
						type="button"
					>
						×
					</button>
				</div>
			</div>
		</div>
	);
}

export function WidgetAppearanceEditor({
	brandingHtml,
	brandingRemovalAllowed,
	onChange,
	value,
}: WidgetAppearanceEditorProps) {
	function updatePanel<Key extends keyof WidgetPanelAppearance>(
		key: Key,
		updated: WidgetPanelAppearance[Key],
	): void {
		onChange({
			...value,
			appearance: {
				...value.appearance,
				panel: { ...value.appearance.panel, [key]: updated },
			},
		});
	}

	function updateBubble(
		owner: 'agentBubble' | 'visitorBubble',
		updated: Partial<WidgetBubbleAppearance>,
	): void {
		onChange({
			...value,
			appearance: {
				...value.appearance,
				[owner]: { ...value.appearance[owner], ...updated },
			},
		});
	}

	function updateSharedBubbleStyle(
		updated: Partial<Pick<WidgetBubbleAppearance, 'fontSize' | 'radius' | 'shape'>>,
	): void {
		onChange({
			...value,
			appearance: {
				...value.appearance,
				agentBubble: { ...value.appearance.agentBubble, ...updated },
				visitorBubble: { ...value.appearance.visitorBubble, ...updated },
			},
		});
	}

	function updateLauncher<Key extends keyof WidgetLauncherAppearance>(
		key: Key,
		updated: WidgetLauncherAppearance[Key],
	): void {
		onChange({
			...value,
			appearance: {
				...value.appearance,
				launcher: { ...value.appearance.launcher, [key]: updated },
			},
		});
	}

	function updateMenu<Key extends keyof WidgetMenuSettings>(
		key: Key,
		updated: WidgetMenuSettings[Key],
	): void {
		onChange({
			...value,
			menu: { ...value.menu, [key]: updated },
		});
	}

	return (
		<section className="chat-box-customisation">
			<div className="chat-box-editor-toolbar">
				<button
					className="secondary-button"
					onClick={() => {
						const defaults = defaultWidgetSettings();
						onChange({
							...defaults,
							availabilityMode: value.availabilityMode,
							offlineForm: value.offlineForm,
						});
					}}
					type="button"
				>
					Reset to defaults
				</button>
			</div>

			<div className="chat-box-customisation-layout">
				<div className="chat-box-customisation-controls">
					<section className="settings-card integration-panel chat-box-settings-panel">
						<div className="integration-panel-heading">
							<h4 className="panel-title">General Settings</h4>
							<p>Set the visitor-facing copy, location and overall Chatbox shape.</p>
						</div>
						<div className="chat-box-settings-fields">
							<label>
								<span>Title</span>
								<input
									maxLength={80}
									onChange={(event) =>
										onChange({ ...value, title: event.currentTarget.value })
									}
									required
									value={value.title}
								/>
							</label>
							<label>
								<span>Subtitle</span>
								<input
									maxLength={160}
									onChange={(event) =>
										onChange({ ...value, subtitle: event.currentTarget.value })
									}
									required
									value={value.subtitle}
								/>
							</label>
							<label>
								<span>Start heading</span>
								<input
									maxLength={80}
									onChange={(event) =>
										onChange({
											...value,
											startTitle: event.currentTarget.value,
										})
									}
									placeholder="Optional"
									value={value.startTitle}
								/>
								<small>Leave empty to hide this heading.</small>
							</label>
							<label>
								<span>Start message</span>
								<textarea
									maxLength={240}
									onChange={(event) =>
										onChange({
											...value,
											startMessage: event.currentTarget.value,
										})
									}
									placeholder="Optional"
									value={value.startMessage}
								/>
								<small>Leave empty to hide this message.</small>
							</label>
							<label>
								<span>Message placeholder</span>
								<input
									maxLength={160}
									onChange={(event) =>
										onChange({
											...value,
											messagePlaceholder: event.currentTarget.value,
										})
									}
									required
									value={value.messagePlaceholder}
								/>
							</label>
							<label>
								<span>Start button</span>
								<input
									maxLength={40}
									onChange={(event) =>
										onChange({
											...value,
											startButton: event.currentTarget.value,
										})
									}
									required
									value={value.startButton}
								/>
							</label>
							<label>
								<span>Position</span>
								<select
									onChange={(event) =>
										onChange({
											...value,
											position: event.currentTarget
												.value as WidgetSettings['position'],
										})
									}
									value={value.position}
								>
									<option value="right">Bottom right</option>
									<option value="left">Bottom left</option>
								</select>
							</label>
							<ColourControl
								label="Theme colour"
								onChange={(accentColour) => onChange({ ...value, accentColour })}
								value={value.accentColour}
							/>
							<RangeControl
								label="Chatbox radius"
								max={48}
								onChange={(updated) => updatePanel('radius', updated)}
								value={value.appearance.panel.radius}
							/>
							{brandingRemovalAllowed && (
								<label className="switch-row compact-switch chat-box-branding-switch">
									<div className="settings-option-copy">
										<span className="settings-option-title">
											Remove branding
										</span>
										<span className="settings-option-description">
											Hide the platform branding beneath this Chatbox.
										</span>
									</div>
									<input
										checked={value.hideBranding}
										onChange={(event) =>
											onChange({
												...value,
												hideBranding: event.currentTarget.checked,
											})
										}
										type="checkbox"
									/>
								</label>
							)}
						</div>
					</section>

					<section className="settings-card integration-panel chat-box-settings-panel">
						<div className="integration-panel-heading">
							<h4 className="panel-title">Chat panel</h4>
							<p>
								Control the conversation background, typing area and shared message
								style.
							</p>
						</div>
						<div className="chat-box-settings-fields">
							<ColourControl
								label="Background"
								onChange={(updated) => updatePanel('backgroundColour', updated)}
								value={value.appearance.panel.backgroundColour}
							/>
							<ColourControl
								label="Typing area"
								onChange={(updated) => updatePanel('surfaceColour', updated)}
								value={value.appearance.panel.surfaceColour}
							/>
							<ColourControl
								label="Text"
								onChange={(updated) => updatePanel('textColour', updated)}
								value={value.appearance.panel.textColour}
							/>
							<RangeControl
								label="Font size"
								max={24}
								min={10}
								onChange={(fontSize) => updateSharedBubbleStyle({ fontSize })}
								value={value.appearance.visitorBubble.fontSize}
							/>
							<RangeControl
								label="Bubble radius"
								max={32}
								onChange={(radius) => updateSharedBubbleStyle({ radius })}
								value={value.appearance.visitorBubble.radius}
							/>
							<label>
								<span>Bubble shape</span>
								<select
									onChange={(event) =>
										updateSharedBubbleStyle({
											shape: event.currentTarget
												.value as WidgetBubbleAppearance['shape'],
										})
									}
									value={value.appearance.visitorBubble.shape}
								>
									<option value="speech">Speech</option>
									<option value="rounded">Square</option>
								</select>
							</label>
						</div>
					</section>

					{(['visitorBubble', 'agentBubble'] as const).map((owner) => {
						const bubble = value.appearance[owner];
						const title =
							owner === 'visitorBubble' ? 'Visitor messages' : 'Agent messages';
						return (
							<section
								className="settings-card integration-panel chat-box-settings-panel"
								key={owner}
							>
								<div className="integration-panel-heading">
									<h4 className="panel-title">{title}</h4>
									<p>Choose the colours for these message bubbles.</p>
								</div>
								<div className="chat-box-settings-fields">
									<ColourControl
										label="Background"
										onChange={(backgroundColour) =>
											updateBubble(owner, { backgroundColour })
										}
										value={bubble.backgroundColour}
									/>
									<ColourControl
										label="Text"
										onChange={(textColour) =>
											updateBubble(owner, { textColour })
										}
										value={bubble.textColour}
									/>
								</div>
							</section>
						);
					})}

					<section className="settings-card integration-panel chat-box-settings-panel">
						<div className="integration-panel-heading">
							<h4 className="panel-title">Launcher button</h4>
							<p>Style the button visitors use to open and close the Chatbox.</p>
						</div>
						<div className="chat-box-settings-fields">
							<ColourControl
								label="Background"
								onChange={(updated) => updateLauncher('backgroundColour', updated)}
								value={value.appearance.launcher.backgroundColour}
							/>
							<ColourControl
								label="Icon"
								onChange={(updated) => updateLauncher('iconColour', updated)}
								value={value.appearance.launcher.iconColour}
							/>
							<RangeControl
								label="Button radius"
								max={48}
								onChange={(updated) => updateLauncher('radius', updated)}
								value={value.appearance.launcher.radius}
							/>
						</div>
					</section>

					<section className="settings-card integration-panel chat-box-settings-panel">
						<div className="integration-panel-heading">
							<h4 className="panel-title">Menu</h4>
							<p>
								Choose whether visitors see the Chatbox menu and which options it
								contains.
							</p>
						</div>
						<div className="settings-switches chat-box-menu-switches">
							<label className="switch-row">
								<div className="settings-option-copy">
									<span className="settings-option-title">Menu visible</span>
									<span className="settings-option-description">
										Show the three-dot menu beside the minimize button.
									</span>
								</div>
								<input
									checked={value.menu.visible}
									onChange={(event) =>
										updateMenu('visible', event.currentTarget.checked)
									}
									type="checkbox"
								/>
							</label>
							{value.menu.visible && (
								<>
									<label className="switch-row">
										<div className="settings-option-copy">
											<span className="settings-option-title">
												Email chat transcript
											</span>
											<span className="settings-option-description">
												Let visitors email themselves a copy of the
												conversation.
											</span>
										</div>
										<input
											checked={value.menu.emailTranscript}
											onChange={(event) =>
												updateMenu(
													'emailTranscript',
													event.currentTarget.checked,
												)
											}
											type="checkbox"
										/>
									</label>
									<label className="switch-row">
										<div className="settings-option-copy">
											<span className="settings-option-title">Sound</span>
											<span className="settings-option-description">
												Let visitors turn the incoming-message sound on or
												off.
											</span>
										</div>
										<input
											checked={value.menu.sound}
											onChange={(event) =>
												updateMenu('sound', event.currentTarget.checked)
											}
											type="checkbox"
										/>
									</label>
									<label className="switch-row">
										<div className="settings-option-copy">
											<span className="settings-option-title">End chat</span>
											<span className="settings-option-description">
												Let visitors close the active conversation.
											</span>
										</div>
										<input
											checked={value.menu.endChat}
											onChange={(event) =>
												updateMenu('endChat', event.currentTarget.checked)
											}
											type="checkbox"
										/>
									</label>
								</>
							)}
						</div>
					</section>
				</div>

				<aside className="settings-card integration-panel chat-box-preview-panel">
					<div className="integration-panel-heading">
						<h4 className="panel-title">Preview</h4>
						<p>This panel stays visible while you work through the settings.</p>
					</div>
					<ChatboxPreview brandingHtml={brandingHtml} settings={value} />
				</aside>
			</div>
		</section>
	);
}
